import { yupResolver } from '@hookform/resolvers/yup';
import { FormEventHandler, MutableRefObject, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ChapterNumber,
  CommentDivision,
  Company,
  Destination,
  DevelopmentScale,
  Language,
  Level,
  OriSearchConditions,
  ProductionDivision,
  TreeCompany,
} from '../../../../../api/model';
import yup from '../../../../../utils/custom-yup';
import DatePicker from '../../../../atoms/DatePicker';
import MultiSelect from '../../../../atoms/MultiSelect';
import OrganizationTreeMultiSelect, { getOptions } from '../../../../modules/OrganizationTreeMultiSelect';
import MultiSelectStringArray from '../../../schedulelist/MultiSelectStringArray';

interface Props {
  defaultConditions: OriSearchConditions;
  conditionsRef: MutableRefObject<OriSearchConditions>;
  productionDivisions: ProductionDivision[];
  modelNames: string[];
  destinations: Destination[];
  modelUsers: TreeCompany[];
  companies: Company[];
  developmentScales: DevelopmentScale[];
  languages: Language[];
  chapterNumbers: ChapterNumber[];
  levels: Level[];
  commentDivisions: CommentDivision[];
  isBrowsing: boolean;
  setConditionsAndSearch: (value: OriSearchConditions) => void;
}

function OriSearchCondition({
  defaultConditions,
  conditionsRef,
  productionDivisions,
  modelNames,
  destinations,
  modelUsers,
  companies,
  developmentScales,
  languages,
  chapterNumbers,
  levels,
  commentDivisions,
  isBrowsing,
  setConditionsAndSearch,
}: Props) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // フォームのデフォルト値
  const defaultValues = useMemo(() => {
    const modelUserIds = defaultConditions.model_user_ids?.map((x) => `user_${x}`);
    return { ...defaultConditions, model_user_ids: modelUserIds };
  }, [defaultConditions]);

  // フォーム情報
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ps_no: '',
      production_no: '',
      production_division_ids: [],
      model_names: [],
      destination_ids: [],
      sales_my: '',
      model_user_ids: [],
      maker_company_ids: [],
      development_scale_ids: [],
      language_ids: [],
      title: '',
      chapter_number_ids: [],
      levels: [],
      plain_text: '',
      comment: '',
      complete_on_from: '',
      complete_on_end: '',
    },
  });

  // フォーム送信
  const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    void handleSubmit((data) => {
      // 組織コンポネントを使っているため、user_{number} の形でデータが送られるので number に変換する
      const modelUserIds =
        data.model_user_ids != null
          ? data.model_user_ids.flatMap((x) => {
              const splited = String(x).split('_');
              return splited.length === 2 ? parseInt(splited[1], 10) : [];
            })
          : undefined;
      const next: OriSearchConditions = {
        ...conditionsRef.current,
        ps_no: data.ps_no,
        production_no: data.production_no,
        production_division_ids: data.production_division_ids,
        model_names: data.model_names,
        destination_ids: data.destination_ids,
        sales_my: data.sales_my,
        model_user_ids: modelUserIds,
        maker_company_ids: data.maker_company_ids,
        development_scale_ids: data.development_scale_ids,
        language_ids: data.language_ids,
        title: data.title,
        chapter_number_ids: data.chapter_number_ids,
        levels: data.levels,
        plain_text: data.plain_text,
        comment: data.comment,
        complete_on_from: data.complete_on_from,
        complete_on_end: data.complete_on_end,
      };

      // 検索実行など
      setConditionsAndSearch(next);
    })();
  };

  return (
    <form id="sections-search-conditions" onSubmit={onSubmit}>
      <div className="sepfr3 mbs">
        <div className="labip1">
          <div>
            <label htmlFor="ps_no">{t('PS管理No.')}</label>
            <div>
              <input type="text" id="ps_no" {...register('ps_no')} />
            </div>
          </div>
          <div>
            <label htmlFor="production_no">{t('資料管理No')}</label>
            <input type="text" id="production_no" {...register('production_no')} />
          </div>
          <div>
            <label htmlFor="production_division_ids">{t('資料区分')}</label>
            <MultiSelect
              id="production_division_ids"
              name="production_division_ids"
              options={productionDivisions}
              control={control}
              isDisabled={false}
              getValue={(x) => x.production_division_id}
              getLabel={(x) => x.production_division_name}
            />
          </div>
          <div>
            <label htmlFor="model_name">{t('機種名')}</label>
            <MultiSelectStringArray
              id="model_names"
              name="model_names"
              options={modelNames}
              control={control}
              isDisabled={false}
              getValue={(x) => x}
              getLabel={(x) => x}
            />
          </div>
          <div>
            <label htmlFor="destination_ids">{t('仕向')}</label>
            <MultiSelect
              id="destination_ids"
              name="destination_ids"
              options={destinations}
              control={control}
              isDisabled={false}
              getValue={(x) => x.destination_id}
              getLabel={(x) => x.destination_name}
            />
          </div>
          <div>
            <label htmlFor="sales_my">{t('M/Y')}</label>
            <input type="text" id="sales_my" {...register('sales_my')} />
          </div>
        </div>
        <div className="labip1">
          <div>
            <label htmlFor="model_user_ids">{t('担当者')}</label>
            <span className="dropdown">
              <OrganizationTreeMultiSelect
                id="model_user_ids"
                name="model_user_ids"
                control={control}
                options={getOptions(modelUsers, language === 'ja')}
                selectableTypes={['user']}
                isDisabled={false}
              />
            </span>
          </div>
          <div>
            <label htmlFor="maker_company_ids">{t('原作メーカー')}</label>
            <MultiSelect
              id="maker_company_ids"
              name="maker_company_ids"
              options={companies}
              control={control}
              isDisabled={false}
              getValue={(x) => x.company_id}
              getLabel={(x) => x.company_name}
            />
          </div>
          <div>
            <label htmlFor="development_scale_ids">{t('開発規模')}</label>
            <MultiSelect
              id="development_scale_ids"
              name="development_scale_ids"
              options={developmentScales}
              control={control}
              isDisabled={false}
              getValue={(x) => x.development_scale_id}
              getLabel={(x) => x.development_scale_name}
            />
          </div>
          <div>
            <label htmlFor="language_ids">{t('言語')}</label>
            <MultiSelect
              id="language_ids"
              name="language_ids"
              options={languages}
              control={control}
              isDisabled={false}
              getValue={(x) => x.language_id}
              getLabel={(x) => x.language_name}
            />
          </div>
          <div>
            <label htmlFor="title">{t('タイトル')}</label>
            <input type="text" id="title" {...register('title')} />
          </div>
          <div>
            <label htmlFor="chapter_number_ids">{t('章')}</label>
            <MultiSelect
              id="chapter_number_ids"
              name="chapter_number_ids"
              options={chapterNumbers}
              control={control}
              isDisabled={false}
              getValue={(x) => x.chapter_number_id}
              getLabel={(x) => x.chapter_name}
            />
          </div>
        </div>
        <div className="labip1">
          <div>
            <label htmlFor="levels">{t('レベル')}</label>
            <MultiSelect
              id="levels"
              name="levels"
              options={levels}
              control={control}
              isDisabled={false}
              getValue={(x) => x.level_id}
              getLabel={(x) => x.level_name}
            />
          </div>
          <div>
            <label htmlFor="plain_text">{t('文章')}</label>
            <input type="text" id="plain_text" {...register('plain_text')} />
          </div>
          <div>
            <label htmlFor="comment">{t('コメント')}</label>
            <input type="text" id="comment" {...register('comment')} />
          </div>
          <div>
            <label htmlFor="complete_on_from">{t('完成日From')}</label>
            <DatePicker
              id="complete_on_from"
              name="complete_on_from"
              isDisabled={false}
              control={control}
              displayMark
            />
          </div>
          <div>
            <label htmlFor="complete_on_end">{t('完成日End')}</label>
            <DatePicker id="complete_on_end" name="complete_on_end" isDisabled={false} control={control} displayMark />
          </div>
        </div>
      </div>
      <div className="controlfr" style={{ marginTop: '10px' }}>
        <span>
          <input
            type="submit"
            id=""
            name=""
            value={t('検索').toString()}
            className="searchbtn"
            disabled={!isBrowsing}
          />
        </span>
      </div>
    </form>
  );
}

export default OriSearchCondition;
